import { getEnvAndRegionFromOrigin } from "@/utils/env-utils";
import {
  convertFromMeter,
  convertToMeter,
  formatFractionNumber,
  MeasurementUnits,
  metersToFormattedFractional,
} from "@faro-lotv/app-component-toolbox";
import {
  DistanceUnitTypes,
  isLocalhost,
  validDistanceUnitKey,
} from "@faro-lotv/foundation";
import { SettingsApiClient } from "@stellar/settings-api-client";

/**
 * @param unitType The unit type to check
 * @returns True if the unit type is metric, false otherwise
 */
export function isMetric(unitType: DistanceUnitTypes): boolean {
  switch (unitType) {
    case DistanceUnitTypes.millimeters:
    case DistanceUnitTypes.meters:
      return true;
    default:
      return false;
  }
}

/**
 * @param unitType The unit type
 * @returns Unit symbol text for the given unit type
 */
export function getDistanceUnitSymbolText(unitType: DistanceUnitTypes): string {
  switch (unitType) {
    case DistanceUnitTypes.millimeters:
      return "mm";
    case DistanceUnitTypes.meters:
      return "m";
    case DistanceUnitTypes.feetFractionalInches:
    case DistanceUnitTypes.decimalFeet:
      return "ft";
    case DistanceUnitTypes.decimalUSSurveyFeet:
      return "ft\u00A0(US)";
    case DistanceUnitTypes.fractionalInches:
    case DistanceUnitTypes.decimalInches:
      return "in";
    default:
      return "m";
  }
}

type FormatDistanceOptions = {
  /**
   * Whether to force small units that make short distances more readable.
   * This should be enabled in contexts where small distances are expected.
   *
   * @default false
   */
  useSmallUnits?: boolean;
  /**
   * Use an additional decimal digit if the millimeter unit type is used.
   */
  useIncreasedMMPrecision?: boolean;
};

/**
 * Formats a distance value into a string representation based on the specified unit type
 *
 * @param distanceInMeters The distance value in meters
 * @param unitType The unit type to format the distance into
 * @returns The formatted distance string
 */
export function formatDistance(
  distanceInMeters: number,
  unitType: DistanceUnitTypes,
  {
    useSmallUnits = false,
    useIncreasedMMPrecision = false,
  }: FormatDistanceOptions = {},
): string {
  let adjustedUnitType = unitType;
  if (useSmallUnits) {
    // We only adjust decimal values to the corresponding smaller unit for better readability.
    // We do not adjust US Survey Feet as it's a very specific unit where users expect consistent use.
    // We do not adjust the fractional types, as they drop leading zeros and thus 'feet with
    // fractional inches'  matches 'fractional inches' for values below 1 foot.
    if (isMetric(unitType)) {
      adjustedUnitType = DistanceUnitTypes.millimeters;
    } else if (unitType === DistanceUnitTypes.decimalFeet) {
      adjustedUnitType = DistanceUnitTypes.decimalInches;
    }
  }

  switch (adjustedUnitType) {
    case DistanceUnitTypes.millimeters:
      // Millimeters (integer) | 1234 mm
      return `${(distanceInMeters * 1000).toFixed(useIncreasedMMPrecision ? 1 : 0)}\u00A0mm`;
    case DistanceUnitTypes.meters:
      // Meters (3 digits) | 1.234 m
      return `${distanceInMeters.toFixed(3)}\u00A0m`;
    case DistanceUnitTypes.feetFractionalInches:
      // Feet + Fractional Inches | 71′ 6 1⁄8″
      return metersToFormattedFractional({
        meters: distanceInMeters,
        precision: 16,
      });
    case DistanceUnitTypes.decimalFeet: {
      // Decimal Feet (3 decimal places) | 71.125′
      const feet = convertFromMeter(distanceInMeters, MeasurementUnits.feet);
      return `${feet.toFixed(3)}′`;
    }
    case DistanceUnitTypes.decimalUSSurveyFeet: {
      // Decimal US Survey Feet (3 decimal places) | 71.125 ft (US)
      return `${convertToUSSurveyFeet(distanceInMeters).toFixed(3)}\u00A0ft\u00A0(US)`;
    }
    case DistanceUnitTypes.fractionalInches: {
      // Fractional Inches | 62 1⁄8″
      const inches = convertFromMeter(
        distanceInMeters,
        MeasurementUnits.inches,
      );
      return `${formatFractionNumber(inches, 16)}″`;
    }
    case DistanceUnitTypes.decimalInches: {
      // Decimal Inches (2 digits) | 62.13″
      const inches = convertFromMeter(
        distanceInMeters,
        MeasurementUnits.inches,
      );
      return `${inches.toFixed(2)}″`;
    }
    default:
      return `${distanceInMeters}\u00B2m`;
  }
}

/**
 * Formats an area value into a string representation based on the specified unit type.
 *  - All SI units will be formated using square meters, e.g. `1.234 m²`
 *  - All Imperial units will be formated using square feet, e.g. `13.283 ft²`
 *
 * @param areaInMetersSq The area value in square meters
 * @param unitType The unit type to format the area into
 * @returns The formatted area string
 */
export function formatArea(
  areaInMetersSq: number,
  unitType: DistanceUnitTypes,
): string {
  switch (unitType) {
    case DistanceUnitTypes.millimeters:
    case DistanceUnitTypes.meters:
      return `${areaInMetersSq.toFixed(3)}\u00A0m²`;
    case DistanceUnitTypes.feetFractionalInches:
    case DistanceUnitTypes.decimalFeet:
    case DistanceUnitTypes.fractionalInches:
    case DistanceUnitTypes.decimalInches: {
      const area = areaInMetersSq * SQUARE_METER_TO_SQUARE_FOOT;
      return `${area.toFixed(3)}\u00A0ft²`;
    }
    case DistanceUnitTypes.decimalUSSurveyFeet: {
      const area = areaInMetersSq * SQUARE_METER_TO_SQUARE_US_SURVEY_FOOT;
      return `${area.toFixed(3)}\u00A0ft²\u00A0(US)`;
    }
    default:
      return `${areaInMetersSq}\u00A0m²`;
  }
}

/**
 * Formats a distance value into a string. The result will be always be decimal
 * value in the specified unit with optional unit symbol, regardless fractional
 * type.
 *
 * @param distanceInMeters The distance value in meters
 * @param unitType The unit type to format the distance into
 * @param unitSymbol Whether to include the unit symbol in the output.
 * @returns The formatted distance string
 */
export function formatDistanceDecimal(
  distanceInMeters: number,
  unitType: DistanceUnitTypes,
  unitSymbol: boolean,
): string {
  switch (unitType) {
    case DistanceUnitTypes.millimeters:
      return `${(distanceInMeters * 1000).toFixed(0)}${unitSymbol ? "\u00A0mm" : ""}`;
    case DistanceUnitTypes.meters:
      return `${distanceInMeters.toFixed(3)}${unitSymbol ? "\u00A0m" : ""}`;
    case DistanceUnitTypes.feetFractionalInches:
    case DistanceUnitTypes.decimalFeet: {
      const feet = convertFromMeter(distanceInMeters, MeasurementUnits.feet);
      return `${feet.toFixed(3)}${unitSymbol ? "′" : ""}`;
    }
    case DistanceUnitTypes.decimalUSSurveyFeet: {
      return `${convertToUSSurveyFeet(distanceInMeters).toFixed(3)}${unitSymbol ? "\u00A0ft\u00A0(US)" : ""}`;
    }
    case DistanceUnitTypes.fractionalInches:
    case DistanceUnitTypes.decimalInches: {
      const inches = convertFromMeter(
        distanceInMeters,
        MeasurementUnits.inches,
      );
      return `${inches.toFixed(2)}${unitSymbol ? "″" : ""}`;
    }
    default:
      return `${distanceInMeters}`;
  }
}

// 1 US Survey Foot is defined as 1200/3937 meter exactly
// https://www.nist.gov/pml/us-surveyfoot
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const METER_TO_US_SURVEY_FOOT = 3937 / 1200;
const SQUARE_METER_TO_SQUARE_US_SURVEY_FOOT =
  METER_TO_US_SURVEY_FOOT * METER_TO_US_SURVEY_FOOT;

/**
 * @param distanceInMeters The distance in meters
 * @returns The distance in US Survey Feet
 */
function convertToUSSurveyFeet(distanceInMeters: number): number {
  return distanceInMeters * METER_TO_US_SURVEY_FOOT;
}

// 1 feet is 0.3048 meter
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const METER_TO_FOOT = 1 / 0.3048;
const SQUARE_METER_TO_SQUARE_FOOT = METER_TO_FOOT * METER_TO_FOOT;
const CUBIC_METER_TO_CUBIC_FOOT = SQUARE_METER_TO_SQUARE_FOOT * METER_TO_FOOT;

/**
 * @param cubicMeters The volume in cubic meters
 * @returns The volume in cubic feet
 */
export function convertCubicMetersToCubicFeet(cubicMeters: number): number {
  return cubicMeters * CUBIC_METER_TO_CUBIC_FOOT;
}

/**
 * @returns The converted distance value for given unit to meter
 * @param distance Distance value in the given unit
 * @param unitType The unit type of the distance value
 */
export function convertDistanceToMeter(
  distance: number,
  unitType: DistanceUnitTypes,
): number {
  switch (unitType) {
    case DistanceUnitTypes.millimeters:
      return distance / 1000;
    case DistanceUnitTypes.meters:
      return distance;
    case DistanceUnitTypes.feetFractionalInches:
    case DistanceUnitTypes.decimalFeet:
      return convertToMeter(distance, MeasurementUnits.feet);
    case DistanceUnitTypes.decimalUSSurveyFeet:
      return distance / METER_TO_US_SURVEY_FOOT;
    case DistanceUnitTypes.fractionalInches:
    case DistanceUnitTypes.decimalInches:
      return convertToMeter(distance, MeasurementUnits.inches);
    default:
      return distance;
  }
}

/**
 * Fetches the user's distance unit setting from the settings API client.
 *
 * @param settingsApiClient The settings API client to use for fetching the setting
 * @returns A promise that resolves to the user's distance unit setting, or undefined if the setting could not be fetched or is invalid
 */
export async function fetchDistanceUnitSetting(
  settingsApiClient: SettingsApiClient,
): Promise<DistanceUnitTypes | undefined> {
  try {
    const { value } =
      await settingsApiClient.user.fetchUserSettings("unit_measurement");

    if (validDistanceUnitKey(value)) {
      return DistanceUnitTypes[value];
    }
  } catch (error) {
    console.error(error);
  }
}

/**
 * Gets the default distance unit based on the current environment.
 *
 * @returns The default distance unit.
 */
export function getDefaultUnit(): DistanceUnitTypes {
  if (isLocalhost(location.hostname)) {
    return DistanceUnitTypes.meters;
  }

  const [, reg] = getEnvAndRegionFromOrigin(location.origin);
  return reg === "eu"
    ? DistanceUnitTypes.meters
    : DistanceUnitTypes.feetFractionalInches;
}
